import { useState, useEffect } from "react";
import { Check, X, Search, Save, AlertCircle } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../app/AuthContext";
import { useSchoolProfile } from "../../../app/SchoolProfileContext";
import { queryKeys } from "../../../lib/queryClient";
import {
  getStudents,
  getEnrollments,
  getGrades,
  saveGrade,
  updateGrade,
} from "../../../lib/api";
import type { ActivityGradeData, StudentData } from "../../../lib/api";

interface ActivityCheckInProps {
  activityId: string;
  activityTitle: string;
  classId: string;
  className: string;
  onClose: () => void;
}

export default function ActivityCheckIn({
  activityId,
  activityTitle,
  classId,
  className,
  onClose,
}: ActivityCheckInProps) {
  const { token } = useAuth();
  const { activeProfile } = useSchoolProfile();
  const queryClient = useQueryClient();
  const [students, setStudents] = useState<StudentData[]>([]);
  const [grades, setGrades] = useState<Record<string, ActivityGradeData>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchClassData = async () => {
    if (!token) return;
    try {
      setLoading(true);

      const [allStudents, enrollments, activityGrades] = await Promise.all([
        getStudents(token, activeProfile?.id),
        getEnrollments(token, activeProfile?.id),
        getGrades(token, activityId),
      ]);

      // Map grades by student_id
      const gradesMap: Record<string, ActivityGradeData> = {};
      activityGrades.forEach((g) => {
        gradesMap[g.student_id] = g;
      });
      setGrades(gradesMap);

      const classEnrollments = enrollments.filter(
        (e) => e.class_id === classId,
      );
      const studentIds = new Set(classEnrollments.map((e) => e.student_id));
      const classStudents = allStudents.filter((s) => studentIds.has(s.id));
      setStudents(classStudents);
    } catch (err) {
      console.error("Failed to load check-in data", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClassData();
  }, [token, activityId, classId, activeProfile?.id]);

  const [searchTerm, setSearchTerm] = useState("");

  const handleToggleStatus = (studentId: string) => {
    const current = grades[studentId];
    const newStatus = current?.status === "submitted" ? "pending" : "submitted";

    // Optimization: update local state IMMEDIATELY for fast visual feedback
    setGrades((prev) => ({
      ...prev,
      [studentId]: {
        ...(prev[studentId] || {
          id: "",
          activity_id: activityId,
          student_id: studentId,
        }),
        status: newStatus,
      },
    }));

    handleSave(studentId, { status: newStatus });
  };

  const handleGradeChange = (studentId: string, score: string) => {
    const numScore = parseFloat(score);
    if (isNaN(numScore)) {
      if (score === "") {
        setGrades((prev) => ({
          ...prev,
          [studentId]: {
            ...(prev[studentId] || {
              id: "",
              activity_id: activityId,
              student_id: studentId,
              status: "pending",
            }),
            score: undefined,
          },
        }));
      }
      return;
    }

    setGrades((prev) => ({
      ...prev,
      [studentId]: {
        ...(prev[studentId] || {
          id: "",
          activity_id: activityId,
          student_id: studentId,
          status: "pending",
        }),
        score: numScore,
      },
    }));
  };

  const handleSave = async (
    studentId: string,
    changes: Partial<ActivityGradeData>,
  ) => {
    if (!token) return;

    // Note: The local state has already been updated by the calling function (handleToggleStatus or handleGradeChange)
    // This ensures the "faster" visual feedback the user requested.

    try {
      const existing = grades[studentId];
      let saved: ActivityGradeData;

      if (existing && existing.id) {
        saved = await updateGrade(existing.id, changes, token);
      } else {
        saved = await saveGrade(
          {
            activity_id: activityId,
            student_id: studentId,
            status: changes.status || (existing?.status as any) || "pending",
            score:
              changes.score !== undefined ? changes.score : existing?.score,
            feedback:
              changes.feedback !== undefined
                ? changes.feedback
                : existing?.feedback,
          },
          token,
        );
      }

      // Update with real server data (which should now match the optimistic one)
      setGrades((prev) => ({ ...prev, [studentId]: saved }));

      // Invalidate dashboard and activity related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.activities.all });
    } catch (err) {
      console.error("Failed to save grade", err);
      // In case of error, maybe revert the local state?
      // For simplicity and speed, let's just log it, but the ideal would be a rollback.
    }
  };

  const filteredStudents = students.filter((s) =>
    s.full_name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-(--card-bg) w-full max-w-4xl rounded-xl shadow-xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-(--border-color) flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-(--text-primary)">
              Registro de Atividade
            </h2>
            <p className="text-(--text-secondary)">
              {activityTitle} • {className}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-(--bg-secondary) rounded-lg transition-colors text-(--text-tertiary) hover:text-(--text-primary)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 border-b border-(--border-color)">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-(--text-tertiary)" />
            <input
              type="text"
              placeholder="Buscar aluno..."
              className="w-full pl-10 pr-4 py-2 border border-(--border-color) rounded-lg bg-(--input-bg) text-(--text-primary) focus:ring-2 focus:ring-(--primary-color) outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-10">Carregando...</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-(--text-secondary)">
                  <th className="pb-3 pl-2">Aluno</th>
                  <th className="pb-3 text-center">Status</th>
                  <th className="pb-3 text-center">Nota (0-10)</th>
                  <th className="pb-3">Feedback</th>
                  <th className="pb-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-(--border-color)">
                {filteredStudents.map((student) => {
                  const grade = grades[student.id];
                  const isSubmitted =
                    grade?.status === "submitted" || grade?.status === "graded";

                  return (
                    <tr
                      key={student.id}
                      className="group hover:bg-(--bg-accent)"
                    >
                      <td className="py-3 pl-2">
                        <div className="font-medium text-(--text-primary)">
                          {student.full_name}
                        </div>
                        {student.email ? (
                          <div className="text-xs text-(--text-tertiary)">
                            {student.email}
                          </div>
                        ) : null}
                      </td>
                      <td className="py-3 text-center">
                        <button
                          onClick={() => handleToggleStatus(student.id)}
                          className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                            isSubmitted
                              ? "bg-green-100 text-green-600 dark:bg-green-900/30"
                              : "bg-gray-100 text-gray-400 dark:bg-gray-800"
                          }`}
                        >
                          {isSubmitted ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <div className="w-2 h-2 rounded-full bg-current" />
                          )}
                        </button>
                      </td>
                      <td className="py-3 text-center">
                        <input
                          type="number"
                          min="0"
                          max="10"
                          step="0.1"
                          className="w-16 text-center px-2 py-1 border border-(--border-color) rounded bg-(--input-bg) text-(--text-primary)"
                          value={grade?.score ?? ""}
                          onChange={(e) =>
                            handleGradeChange(student.id, e.target.value)
                          }
                          onBlur={(e) =>
                            handleSave(student.id, {
                              score: parseFloat(e.target.value),
                            })
                          }
                        />
                      </td>
                      <td className="py-3">
                        <input
                          type="text"
                          placeholder="Adicionar comentário..."
                          className="w-full px-3 py-1 border border-transparent hover:border-(--border-color) rounded bg-transparent focus:bg-(--input-bg) focus:border-(--border-color) text-sm transition-colors text-(--text-primary)"
                          value={grade?.feedback || ""}
                          onChange={(e) =>
                            setGrades((prev) => ({
                              ...prev,
                              [student.id]: {
                                ...(prev[student.id] || {
                                  id: "",
                                  activity_id: activityId,
                                  student_id: student.id,
                                  status: "pending",
                                }),
                                feedback: e.target.value,
                              },
                            }))
                          }
                          onBlur={(e) =>
                            handleSave(student.id, { feedback: e.target.value })
                          }
                        />
                      </td>
                      <td className="py-3 text-right pr-2">
                        {/* Status indicator or save button if needed */}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-4 border-t border-(--border-color) bg-(--bg-secondary) flex justify-between items-center rounded-b-xl">
          <div className="flex items-center gap-2 text-sm text-(--text-secondary)">
            <AlertCircle className="w-4 h-4" />
            <span>As alterações são salvas automaticamente</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-(--primary-color) text-white rounded-lg hover:opacity-90 transition-opacity font-medium"
          >
            Concluir
          </button>
        </div>
      </div>
    </div>
  );
}
